import * as React from 'react';
import { useEffect } from "react";
import { Chart } from 'chart.js';
import { SiteVariablesPrepared } from "@fluentui/react-northstar";
import { IChartData } from "../chart-types";
import { chartConfig, axesConfig, setTooltipColorScheme, numberFormat } from "../chart-utils";
import { ChartContainer } from "./chart-container";
import { chartBarDataPointPatterns } from "../chart-patterns";
import { getText } from "../../../translations";

const PieChart = ({
    title,
    data,
    siteVariables,
    cutoutPercentage,
    customWidth,
    customHeight,
    collapsed,
}: {
    title: string;
    data: IChartData;
    siteVariables: SiteVariablesPrepared;
    cutoutPercentage?: number;
    customWidth?: number;
    customHeight?: number;
    collapsed?: boolean;
}) => {
    if (data && data.datasets && data.datasets[0].data.length > 6) {
        data.datasets[0].data = data.datasets[0].data.slice(0, 6);
        console.warn(
            "Please follow design guidence and apply 6 or less data points per one chart."
        );
    }
    const { colorScheme, theme, colors, t } = siteVariables;
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const chartRef = React.useRef<Chart | undefined>();
    const chartId = React.useMemo(
        () => Math.random().toString(36).substr(2, 9),
        []
    );

    const chartDataPointColors = React.useMemo(
        () => [
            '#508BD5',
            '#FAD869',
            '#ED1B23',
        ],
        [theme]
    );

    const createDataPoints = (): Chart.ChartDataSets[] => {
        let dataPointConfig = {
            label: getText(t?.locale, data.datasets[0].label),
            data: data.datasets[0].data,
            borderWidth: 0,
            borderColor: colorScheme.default.background,
            hoverBorderColor: colorScheme.default.background,
            backgroundColor: chartDataPointColors,
            hoverBackgroundColor: chartDataPointColors,
        };

        return [dataPointConfig] as any;
    };

    useEffect(() => {
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;

        const config: any = chartConfig({ type: "pie" });
        config.options.hover.mode = "point";

        config.options.layout.padding.top = 32;
        config.options.layout.padding.left = -16;
        config.options.layout.padding.right = 32;
        config.options.layout.padding.bottom = 32;

        config.options.scales.xAxes[0].ticks.display = false;
        config.options.scales.xAxes[0].gridLines.display = false;

        config.options.scales.yAxes[0].ticks.display = false;
        config.options.scales.yAxes[0].gridLines.display = false;

        if (cutoutPercentage) {
            config.options.cutoutPercentage = cutoutPercentage;
        }
        // Pie chart custom settings
        config.options.tooltips.callbacks.label = (tooltipItem: any, data: any) =>
            getText(t?.locale, data.labels[tooltipItem.index]);
        config.options.tooltips.callbacks.labelColor = (tooltipItem: any) => ({
            backgroundColor: chartDataPointColors[tooltipItem.index],
        });

        config.options.tooltips.callbacks.title = (tooltipItems: any) => {
            return `${(
                (Number(data.datasets[0].data[tooltipItems[0].index]) /
                    (data.datasets[0].data as number[]).reduce((a, b) => a + b)) *
                100
            ).toPrecision(2)}% (${numberFormat(
                Number(data.datasets[0].data[tooltipItems[0].index])
            )})`;
        };

        chartRef.current = new Chart(ctx, {
            ...(config as any),
            data: {
                labels: Array.isArray(data.labels)
                    ? data.labels.map((label) => getText(t?.locale, label).split(" (")[0])
                    : getText(t?.locale, data.labels),
                datasets: [],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutoutPercentage: 75,
                rotation: (0.1 * Math.PI),
                legend: {
                    display: false,
                },
                tooltips: {
                    mode: 'label',
                    callbacks: {
                        label: function (tooltipItem: any, data: any) {
                            return data['datasets'][0]['data'][tooltipItem['index']] + '%';
                        }
                    }
                },
            }
        });

        return () => {
            if (!chartRef.current) return;
            chartRef.current.destroy();
        };
    }, [data, collapsed]);

    /**
     * Theme updates
     */
    useEffect(() => {
        if (!chartRef.current) return;
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;
        // Apply new colors scheme for data points
        chartRef.current.data.datasets = createDataPoints();
        // Update tooltip colors scheme
        setTooltipColorScheme({
            chart: chartRef.current,
            siteVariables,
            chartDataPointColors,
            patterns: chartBarDataPointPatterns,
            verticalDataAlignment: true,
        });
        // Update axeses
        axesConfig({ chart: chartRef.current, ctx, colorScheme });

        chartRef.current.update();
    }, [theme, data, collapsed]);

    function onLegendClick(datasetIndex: number) {
        if (!chartRef.current) return;
        chartRef.current.update();
    }

    return (
        <ChartContainer
            siteVariables={siteVariables}
            data={data}
            chartDataPointColors={chartDataPointColors}
            patterns={chartBarDataPointPatterns}
            onLegendClick={onLegendClick}
            verticalDataAlignment
            hideLegend={true}
        >
            <canvas
                id={chartId}
                ref={canvasRef}
                tabIndex={0}
                style={{
                    userSelect: "none",
                }}
                aria-label={title}
            >
                {data.datasets.map((set, setKey) =>
                    (set.data as number[]).forEach((item: number, itemKey: number) => (
                        // Generated tooltips for screen readers
                        <div key={itemKey} id={`${chartId}-tooltip-${setKey}-${itemKey}`}>
                            <p>{item}</p>
                            <span>
                                {getText(t?.locale, set.label)}: {set.data[itemKey]}
                            </span>
                        </div>
                    ))
                )}
            </canvas>
        </ChartContainer>
    );
};

export default PieChart;
